import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Users, Luggage, Check, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import VehicleGallery from "../components/VehicleGallery";

const AVAILABILITY = {
  available: { label: "AVAILABLE NOW", cls: "bg-emerald-400 text-emerald-950 shadow-[0_0_20px_rgba(52,211,153,0.6)] font-bold", dot: "bg-emerald-950" },
  limited:   { label: "LIMITED AVAILABILITY", cls: "bg-amber-400 text-amber-950 shadow-[0_0_20px_rgba(251,191,36,0.6)] font-bold", dot: "bg-amber-950" },
  sold_out:  { label: "CURRENTLY SOLD OUT", cls: "bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.6)] font-bold", dot: "bg-white" },
};

export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}`)
      .then((r) => setV(r.data))
      .catch(() => navigate("/fleet"));
  }, [vehicleId, navigate]);

  if (!v) return <div className="h-96 flex items-center justify-center">
    <div className="w-10 h-10 rounded-full border-2 border-cream border-t-transparent animate-spin" />
  </div>;

  const canRide = v.available_for === "chauffeur" || v.available_for === "both";
  const canRent = v.available_for === "rental" || v.available_for === "both";
  const avail = AVAILABILITY[v.availability] || AVAILABILITY.available;

  return (
    <section data-testid="vehicle-detail-page" className="mx-auto max-w-6xl px-5 sm:px-8 py-12">
      <Link to="/fleet" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> Back to fleet
      </Link>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        <div>
          <div className="rounded-3xl overflow-hidden border border-white/10 mb-4">
            <VehicleGallery
              images={v.images}
              image_url={v.image_url}
              alt={v.name}
              aspect="aspect-[16/10]"
              enableLightbox={true}
              testId="vehicle-detail-gallery"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {canRide && <span className="label-caps px-2.5 py-1 rounded-full bg-cream/10 border border-cream/30 text-cream">Chauffeur</span>}
            {canRent && <span className="label-caps px-2.5 py-1 rounded-full bg-platinum/10 border border-platinum/30 text-platinum">Rental</span>}
            {v.rideshare_ready && <span className="label-caps px-2.5 py-1 rounded-full bg-cream/10 border border-cream/30 text-cream">Rideshare & Delivery Approved</span>}
            <span className={`label-caps inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-widest ${avail.cls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${avail.dot}`} /> {avail.label}
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <div className="label-caps text-white/40 mb-1">{v.category.replace(/-/g, " ")}</div>
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight">{v.name}</h1>
            <p className="text-white/70 mt-4 leading-relaxed">{v.description}</p>
          </div>

          <div className="glass rounded-2xl p-5 grid grid-cols-2 gap-4">
            <Metric icon={Users} label="Passengers" value={v.capacity} />
            <Metric icon={Luggage} label="Luggage" value={v.luggage_capacity} suffix=" pcs" />
          </div>

          <div className="glass rounded-2xl p-5 space-y-2.5">
            <div className="label-caps text-cream">Pricing</div>
            {canRide && v.hourly_rate > 0 && <Row k="Chauffeur" v={`$${v.hourly_rate.toFixed(0)}/hr`} />}
            {canRent && <>
              <Row k="Daily" v={`$${v.daily_rate.toFixed(0)}`} />
              <Row k="Weekly" v={`$${v.weekly_rate?.toFixed(0) || "—"}`} />
              <Row k="Monthly" v={`$${v.monthly_rate?.toFixed(0) || "—"}`} />
              <Row k="Min rental" v={`${v.rental_min_days} day${v.rental_min_days > 1 ? "s" : ""}`} />
            </>}
          </div>

          {v.features?.length > 0 && (
            <div className="glass rounded-2xl p-5">
              <div className="label-caps text-cream mb-3">Features</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-white/80">
                {v.features.map((f) => (
                  <div key={f} className="flex items-start gap-1.5">
                    <Check size={14} className="text-cream flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {canRide && (
              <Link data-testid="detail-book-ride" to={`/book/ride/${v.id}`}
                className="btn-cream px-5 py-3 rounded-full text-sm inline-flex items-center gap-2 flex-1 justify-center">
                Book a chauffeured ride <ArrowUpRight size={14} />
              </Link>
            )}
            {canRent && (
              <Link data-testid="detail-book-rental" to={`/book/rental/${v.id}`}
                className="btn-outline-platinum px-5 py-3 rounded-full text-sm inline-flex items-center gap-2 flex-1 justify-center hover:text-white">
                Reserve for self-drive <ArrowUpRight size={14} />
              </Link>
            )}
            {v.rideshare_ready && (
              <Link data-testid="detail-driver-rental" to="/driver-rentals"
                className="w-full px-5 py-2.5 rounded-full text-xs border border-cream/30 text-cream hover:bg-cream/10 inline-flex items-center gap-2 justify-center">
                <Sparkles size={12} /> Rent for Uber, Lyft, DoorDash
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const Metric = ({ icon: Icon, label, value, suffix = "" }) => (
  <div>
    <div className="flex items-center gap-1 text-white/50 mb-1">
      <Icon size={12} className="text-cream" />
      <span className="label-caps text-[10px]">{label}</span>
    </div>
    <div className="font-serif text-2xl">{value}{suffix}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-white/60">{k}</span>
    <span className="text-white font-serif">{v}</span>
  </div>
);
